import { Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { UserService, ChangePasswordModel } from '../../../../shared/services/user.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-change-password-dialog',
    templateUrl: './change-password-dialog.component.html',
    styleUrl: './change-password-dialog.component.css',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslateModule
    ],
    providers: [UserService]
})
export class ChangePasswordDialogComponent {
    changePasswordForm!: FormGroup;
    isLoading = false;
    errorMessage = '';
    hideOldPassword = true;
    hideNewPassword = true;
    hideConfirmPassword = true;

    private formBuilder = inject(FormBuilder);
    private userService = inject(UserService);
    public dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent>);

    ngOnInit(): void {
        this.changePasswordForm = this.formBuilder.group(
            {
                oldPassword: ['', [Validators.required]],
                newPassword: ['', [Validators.required, Validators.minLength(6)]],
                confirmPassword: ['', [Validators.required]]
            },
            { validators: this.passwordMatchValidator }
        );
    }

    passwordMatchValidator(group: FormGroup): { [key: string]: any } | null {
        const newPassword = group.get('newPassword')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        return newPassword === confirmPassword ? null : { passwordMismatch: true };
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSubmit(): void {
        if (this.changePasswordForm.invalid) {
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        const changePasswordModel: ChangePasswordModel = {
            OldPassword: this.changePasswordForm.get('oldPassword')?.value,
            NewPassword: this.changePasswordForm.get('newPassword')?.value,
            ConfirmPassword: this.changePasswordForm.get('confirmPassword')?.value
        };

        this.userService.changePassword(changePasswordModel).subscribe(
            (response) => {
                this.isLoading = false;
                this.dialogRef.close(true);
            },
            (error) => {
                this.isLoading = false;
                if (error.error && error.error.message) {
                    this.errorMessage = error.error.message;
                } else if (error.status === 400) {
                    this.errorMessage = 'Invalid old password or password requirements not met.';
                } else {
                    this.errorMessage = 'An error occurred while changing password. Please try again.';
                }
                // console.error('Change password error:', error);
            }
        );
    }

    togglePasswordVisibility(field: string): void {
        if (field === 'oldPassword') {
            this.hideOldPassword = !this.hideOldPassword;
        } else if (field === 'newPassword') {
            this.hideNewPassword = !this.hideNewPassword;
        } else if (field === 'confirmPassword') {
            this.hideConfirmPassword = !this.hideConfirmPassword;
        }
    }

    getPasswordErrorMessage(): string {
        const newPasswordControl = this.changePasswordForm.get('newPassword');
        if (newPasswordControl?.hasError('required')) {
            return 'Password is required';
        }
        if (newPasswordControl?.hasError('minlength')) {
            return 'Password must be at least 6 characters';
        }
        return '';
    }

    getConfirmPasswordErrorMessage(): string {
        if (this.changePasswordForm.hasError('passwordMismatch')) {
            return 'Passwords do not match';
        }
        return '';
    }
}
